import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  ArrowRight, Users, Target, TrendingUp, Award, ChevronRight, ChevronDown,
  Zap, Rocket, FileText, BarChart3, Shield, Sparkles, Eye, Compass, LineChart,
  Brain, CheckCircle2, Star, Menu, Quote, ExternalLink, Phone, Mail, MapPin,
  Globe, Building2, Handshake, DollarSign, Layers, Settings, UserPlus,
  ArrowDown, Briefcase, Cpu, Megaphone, Network, Calendar
} from "lucide-react";
import { trackCTAClick, trackNavClick, trackScrollDepth, trackPageView } from "@/utils/analytics";
import { usePageSEO } from "@/hooks/usePageSEO";

const SOLUTION_AREAS = [
  {
    id: "strategy", title: "Strategy & Executive Advisory", icon: Compass,
    services: ["Business Strategy", "Business Clarity Assessments", "Strategic Planning", "Growth Strategy", "Executive Advisory", "Crisis Management", "Business Model Evaluation"],
    desc: "Clarify direction, uncover gaps, establish priorities, and develop practical strategies for sustainable growth."
  },
  {
    id: "operations", title: "Operations & Business Systems", icon: Settings,
    services: ["Operational Assessments", "Process Improvement", "SOP Development", "Workflow Optimization", "Business Systems", "Performance Management", "Organizational Infrastructure"],
    desc: "Build systems and processes that improve efficiency, accountability, execution, and scalability."
  },
  {
    id: "technology", title: "Technology, AI & Automation", icon: Cpu,
    services: ["Digital Transformation", "Technology Strategy", "Systems Integration", "AI-Powered Business Solutions", "Workflow Automation", "AI Consulting", "Business Technology Optimization"],
    desc: "Align technology with business objectives and use AI and automation to improve productivity, intelligence, and execution."
  },
  {
    id: "marketing", title: "Marketing & Revenue Growth", icon: Megaphone,
    services: ["Marketing Strategy", "Brand Strategy", "Digital Marketing", "Lead Generation", "Customer Acquisition", "SEO", "Revenue Growth", "Business Development"],
    desc: "Connect marketing and revenue initiatives to the company's larger operational and growth strategy."
  },
  {
    id: "people", title: "People, Staffing & Execution", icon: UserPlus,
    services: ["Remote Staffing", "Specialized Talent", "Project Support", "Implementation Support", "Execution Management", "Resource Coordination"],
    desc: "Connect strategy with the people and execution resources necessary to make the plan happen."
  },
  {
    id: "capital", title: "Capital, Partnerships & Opportunities", icon: Handshake,
    services: ["Strategic Partnerships", "Sponsorship Opportunities", "Funding Resources", "Business Connections", "Growth Opportunities", "Partnership Development"],
    desc: "Help businesses access relationships, resources, capital pathways, and strategic opportunities that can accelerate growth."
  },
];

const PROCESS_STEPS = [
  { step: "Assess", desc: "Understand the business, goals, problems, opportunities, systems, resources, and constraints." },
  { step: "Strategize", desc: "Determine priorities and create a practical roadmap." },
  { step: "Integrate", desc: "Connect the appropriate people, systems, technology, processes, and resources." },
  { step: "Implement", desc: "Move the strategy into execution." },
  { step: "Optimize", desc: "Measure performance, identify friction, and improve the system." },
  { step: "Scale", desc: "Strengthen infrastructure so growth does not create unnecessary chaos." },
];

const BUSINESS_PROBLEMS = [
  "Your business has grown faster than your systems.",
  "Too much depends on the owner.",
  "Your team is busy, but execution is inconsistent.",
  "Your technology does not work together.",
  "You have multiple vendors but no unified strategy.",
  "Your marketing is generating activity without predictable growth.",
  "You need AI or automation but don't know where it belongs.",
  "Your processes live inside people's heads instead of documented systems.",
  "You're constantly reacting instead of operating strategically.",
  "You know where you want the company to go but lack the infrastructure to get there.",
];

const TESTIMONIALS = [
  { quote: "Within 60 days, I went from feeling overwhelmed to having a clear roadmap. The R.A.P.I.D.™ framework completely changed how I approach business decisions.", name: "Sarah M.", role: "Wellness Coach", result: "3x client bookings in 90 days" },
  { quote: "I'd tried other programs before, but this was different. Having a consultant who actually understood my business combined with the AI tools made everything click.", name: "Marcus T.", role: "Creative Agency Owner", result: "First $50K month achieved" },
  { quote: "The clarity assessment alone was worth it. I finally understood why I was stuck and had a concrete plan to move forward.", name: "Jennifer L.", role: "E-commerce Entrepreneur", result: "Doubled revenue in 6 months" }
];

export const LandingPage = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [expandedSolution, setExpandedSolution] = useState(null);

  usePageSEO(
    "Business Consulting, AI & Business Systems | Philadelphia | My Biz Consulting",
    "My Biz Consulting helps specialty contractors, entrepreneurs, influencers, athletes, entertainers, and growing businesses through business strategy, operations, AI, automation, technology, and implementation. Philadelphia, PA."
  );

  useEffect(() => {
    trackPageView('/', 'Landing Page');
    let maxScroll = 0;
    const handleScroll = () => {
      const scrollPercent = Math.round((window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100);
      if (scrollPercent >= 25 && maxScroll < 25) { trackScrollDepth(25); maxScroll = 25; }
      else if (scrollPercent >= 50 && maxScroll < 50) { trackScrollDepth(50); maxScroll = 50; }
      else if (scrollPercent >= 75 && maxScroll < 75) { trackScrollDepth(75); maxScroll = 75; }
      else if (scrollPercent >= 95 && maxScroll < 100) { trackScrollDepth(100); maxScroll = 100; }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* ─── NAV ─── */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-neutral-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-3">
              <img src="/mybiz-logo.png" alt="My Biz Consulting" className="h-10 w-auto" />
              <div>
                <p className="text-sm font-semibold text-neutral-900 leading-tight">MY BIZ CONSULTING℠</p>
                <p className="text-[9px] text-neutral-500 uppercase tracking-widest">Vertically Integrated Business Solutions</p>
              </div>
            </div>

            <div className="hidden lg:flex items-center space-x-5">
              <button onClick={() => document.getElementById('solutions')?.scrollIntoView({ behavior: 'smooth' })} className="text-sm text-neutral-600 hover:text-neutral-900">Solutions</button>
              <button onClick={() => navigate('/pricing')} className="text-sm text-neutral-600 hover:text-neutral-900">Programs</button>
              <button onClick={() => document.getElementById('how-we-work')?.scrollIntoView({ behavior: 'smooth' })} className="text-sm text-neutral-600 hover:text-neutral-900">How We Work</button>
              <button onClick={() => document.getElementById('results')?.scrollIntoView({ behavior: 'smooth' })} className="text-sm text-neutral-600 hover:text-neutral-900">Results</button>
              <button onClick={() => document.getElementById('about')?.scrollIntoView({ behavior: 'smooth' })} className="text-sm text-neutral-600 hover:text-neutral-900">About</button>
              <button onClick={() => navigate('/faq')} className="text-sm text-neutral-600 hover:text-neutral-900">Resources</button>
              <button onClick={() => navigate('/community')} className="text-sm text-neutral-600 hover:text-neutral-900">Community Hub</button>
              <Button variant="ghost" onClick={() => navigate("/login")} className="text-sm" data-testid="nav-login-btn">Login</Button>
              <Button onClick={() => navigate("/clarity-assessment")} className="bg-neutral-900 hover:bg-neutral-800 text-white text-sm" data-testid="nav-cta-btn">
                Get Started
              </Button>
            </div>

            <div className="lg:hidden">
              <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                <SheetTrigger asChild><Button variant="ghost" size="sm" className="p-2"><Menu className="w-6 h-6" /></Button></SheetTrigger>
                <SheetContent side="right" className="w-80 bg-white p-0">
                  <div className="flex flex-col h-full">
                    <div className="p-6 border-b">
                      <p className="text-sm font-semibold">MY BIZ CONSULTING℠</p>
                      <p className="text-[9px] text-[#39FF14] uppercase tracking-widest">Vertically Integrated Business Solutions</p>
                    </div>
                    <nav className="flex-1 p-6 space-y-1">
                      {[
                        { label: "Solutions", action: () => { setMobileMenuOpen(false); document.getElementById('solutions')?.scrollIntoView({ behavior: 'smooth' }); }},
                        { label: "Programs", action: () => { setMobileMenuOpen(false); navigate('/pricing'); }},
                        { label: "How We Work", action: () => { setMobileMenuOpen(false); document.getElementById('how-we-work')?.scrollIntoView({ behavior: 'smooth' }); }},
                        { label: "Results", action: () => { setMobileMenuOpen(false); document.getElementById('results')?.scrollIntoView({ behavior: 'smooth' }); }},
                        { label: "About", action: () => { setMobileMenuOpen(false); document.getElementById('about')?.scrollIntoView({ behavior: 'smooth' }); }},
                        { label: "Business Formation", action: () => { setMobileMenuOpen(false); navigate('/business-formation'); }},
                        { label: "FAQ", action: () => { setMobileMenuOpen(false); navigate('/faq'); }},
                        { label: "Blog", action: () => { setMobileMenuOpen(false); navigate('/blog'); }},
                        { label: "Community Hub", action: () => { setMobileMenuOpen(false); navigate('/community'); }},
                        { label: "Podcast", action: () => { setMobileMenuOpen(false); navigate('/media'); }},
                        { label: "Location", action: () => { setMobileMenuOpen(false); navigate('/location'); }},
                      ].map(item => (
                        <button key={item.label} onClick={item.action} className="w-full text-left px-4 py-3 text-neutral-700 hover:bg-neutral-50 rounded-lg">{item.label}</button>
                      ))}
                    </nav>
                    <div className="p-6 border-t space-y-3">
                      <Button onClick={() => { setMobileMenuOpen(false); navigate("/clarity-assessment"); }} className="w-full bg-neutral-900 text-white">
                        Start Business Clarity Assessment
                      </Button>
                      <Button variant="outline" onClick={() => { setMobileMenuOpen(false); navigate("/login"); }} className="w-full">Login</Button>
                    </div>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
        {/* Brand promise bar */}
        <div className="bg-[#333333] py-1">
          <p className="text-center text-[10px] text-[#39FF14] font-medium tracking-widest uppercase">Consultant-Led &bull; AI-Powered &bull; Results-Driven</p>
        </div>
      </nav>

      {/* ─── 1. HERO ─── */}
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center space-x-2 px-4 py-2 bg-neutral-100 rounded-full mb-8">
            <div className="w-2 h-2 bg-[#39FF14]/50 rounded-full animate-pulse" />
            <span className="text-sm text-neutral-600">Vertically Integrated Business Solutions</span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold text-neutral-900 leading-tight tracking-tight mb-6">
            Philadelphia Business Consulting, AI &amp;
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#39FF14] to-[#32E010]">Integrated Growth Solutions</span>
          </h1>

          <p className="text-lg text-neutral-600 max-w-2xl mx-auto mb-4 leading-relaxed">
            My Biz Consulting helps specialty contractors, entrepreneurs, influencers, athletes, entertainers, 
            creators, and growing businesses build stronger companies through business strategy, operations, AI, 
            automation, technology, marketing, revenue systems, and implementation.
          </p>
          <p className="text-base text-neutral-500 max-w-xl mx-auto mb-10">
            We do not simply advise clients on what to do. We help connect and build the systems needed to execute it.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button size="lg" onClick={() => navigate('/clarity-assessment')}
              className="bg-neutral-900 hover:bg-neutral-800 text-white h-14 px-8 text-base font-medium shadow-lg" data-testid="hero-primary-cta">
              Start With a Business Clarity Assessment <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <a href="https://calendar.app.google/ZJysuiLaRTnTF9pY9" target="_blank" rel="noopener noreferrer">
              <Button size="lg" variant="outline" className="h-14 px-6 text-base border-[#39FF14] text-[#2BC40E] hover:bg-[#39FF14]/10" data-testid="hero-booking-cta">
                <Calendar className="mr-2 h-5 w-5" /> Book a Consultation
              </Button>
            </a>
          </div>

          <p className="text-xs text-neutral-400 mt-6">
            Specialty Contractors &bull; Entrepreneurs &bull; Influencers &bull; Athletes &bull; Entertainers &bull; Growing Businesses
          </p>

          {/* As Seen On Trust Strip */}
          <div className="mt-8 pt-6 border-t border-neutral-100">
            <a href="https://cdn.bmapinc.com/shareables/20038071/as-seen-on/iw_39846-2026-09-16-12-13-48.png" target="_blank" rel="noopener noreferrer" className="block">
              <div className="bg-neutral-900 rounded-xl py-4 px-6 hover:bg-neutral-800 transition-colors">
                <p className="text-[10px] text-[#39FF14] uppercase tracking-widest mb-3 text-center font-medium">As Seen On</p>
                <div className="flex items-center justify-center gap-8">
                  <span className="text-white font-bold text-lg tracking-wider">FOX</span>
                  <span className="text-neutral-600">|</span>
                  <span className="text-white font-bold text-lg tracking-wider">USA Today</span>
                  <span className="text-neutral-600">|</span>
                  <span className="text-white font-bold text-lg tracking-wider">MarketWatch</span>
                  <span className="text-neutral-600">|</span>
                  <span className="text-white font-bold text-lg tracking-wider">CBS</span>
                </div>
              </div>
            </a>
            <div className="mt-4 flex justify-center">
              <a href="https://techbehemoths.com/company/my-biz-consulting-llc" target="_blank" rel="noopener noreferrer" className="hover:opacity-80 transition-opacity">
                <img src="https://cdn.techbehemoths.com/public/static/validation/tb-verified-logo.svg" alt="TechBehemoths Verification" className="h-10 w-auto" width="auto" height="40" />
              </a>
            </div>
          </div>
        </div>

      {/* ─── 2. WHAT VERTICALLY INTEGRATED MEANS ─── */}
      </section>
      <section className="py-16 bg-[#333333] text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold mb-3">One Partner. Connected Solutions.</h2>
            <p className="text-neutral-300 max-w-2xl mx-auto">
              Traditional consulting often stops at recommendations. My Biz Consulting connects strategy to implementation.
            </p>
          </div>

          {/* Process Flow */}
          <div className="flex flex-wrap justify-center items-center gap-2 mb-10">
            {PROCESS_STEPS.map((s, i) => (
              <div key={s.step} className="flex items-center">
                <div className="bg-white/10 border border-white/20 rounded-lg px-4 py-2 text-center">
                  <p className="text-[#39FF14] font-bold text-sm">{s.step.toUpperCase()}</p>
                </div>
                {i < PROCESS_STEPS.length - 1 && <ArrowRight className="w-4 h-4 text-[#39FF14] mx-1 flex-shrink-0" />}
              </div>
            ))}
          </div>

          <p className="text-neutral-400 text-center text-sm max-w-2xl mx-auto mb-10">
            Instead of forcing clients to coordinate separate consultants, technology providers, marketing agencies,
            staffing resources, and implementation partners, My Biz Consulting brings interconnected business needs
            together within one strategic ecosystem.
          </p>

          {/* Benefit Cards */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: Users, title: "Fewer Disconnected Vendors", desc: "Reduce the complexity of managing multiple consultants, agencies, systems, and service providers." },
              { icon: Layers, title: "Strategy Connected to Execution", desc: "Recommendations are developed with implementation, resources, systems, and real-world execution in mind." },
              { icon: Network, title: "One Coordinated Ecosystem", desc: "Strategy, technology, operations, marketing, people, and growth initiatives work together instead of in silos." },
              { icon: TrendingUp, title: "Built to Scale", desc: "Solutions improve current performance while creating infrastructure capable of supporting future growth." },
            ].map(b => (
              <div key={b.title} className="bg-white/5 border border-white/10 rounded-xl p-5">
                <b.icon className="w-6 h-6 text-[#39FF14] mb-3" />
                <h3 className="font-semibold text-white text-sm mb-1">{b.title}</h3>
                <p className="text-xs text-neutral-400">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── WHO WE HELP ─── */}
      <section className="py-16 bg-neutral-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <p className="text-[#2BC40E] font-medium text-sm uppercase tracking-wider mb-2">Who We Help</p>
            <h2 className="text-2xl sm:text-3xl font-bold text-neutral-900 mb-3">Built for Businesses and Personal Brands That Are Ready to Operate Like Businesses</h2>
            <p className="text-neutral-600 text-sm max-w-2xl mx-auto">Whether you&apos;re scaling a contracting company, growing a startup, monetizing an audience, building an athlete brand, or managing an entertainment career, growth creates complexity. We connect the strategy, systems, technology, AI, operations, marketing, and implementation needed to turn that complexity into an organized business.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { title: "Specialty Contractors", desc: "Build operations, systems, branding, automation, lead generation, financial readiness, staffing infrastructure, and scalable workflows.", link: "/contractor-business-consulting", icon: Settings },
              { title: "Entrepreneurs", desc: "Move from disconnected ideas and tools into structured, scalable businesses with strategy, systems, and growth infrastructure.", link: "/entrepreneur-consulting", icon: Rocket },
              { title: "Influencers & Creators", desc: "Convert audience, influence, content, and personal branding into sustainable business infrastructure and revenue.", link: "/influencer-business-consulting", icon: Star },
              { title: "Athletes", desc: "Build businesses and personal brands beyond competition through strategy, brand infrastructure, digital systems, and monetization.", link: "/athlete-business-consulting", icon: Target },
              { title: "Entertainers", desc: "Build the business systems behind your career — artist business strategy, digital platforms, media, monetization, and operations.", link: "/entertainment-business-consulting", icon: Sparkles },
              { title: "Growing Businesses", desc: "Established businesses facing operational, technology, or growth challenges that need integrated solutions — not more disconnected vendors.", link: "/clarity-assessment", icon: TrendingUp },
            ].map(audience => (
              <Card key={audience.title} className="border-neutral-200 hover:shadow-md hover:border-[#39FF14]/30 transition-all group cursor-pointer" onClick={() => navigate(audience.link)}>
                <CardContent className="p-5">
                  <audience.icon className="w-6 h-6 text-[#2BC40E] mb-3" />
                  <h3 className="font-bold text-neutral-900 text-sm mb-2 group-hover:text-[#2BC40E] transition-colors">{audience.title}</h3>
                  <p className="text-xs text-neutral-600">{audience.desc}</p>
                  <p className="text-xs text-[#2BC40E] mt-3 flex items-center gap-1">Learn more <ChevronRight className="w-3 h-3" /></p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ─── 3. BUSINESS PROBLEMS ─── */}
      <section className="py-16 bg-neutral-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-neutral-900 mb-3">
              We Solve the Problems Behind the Problem.
            </h2>
            <p className="text-neutral-600 max-w-xl mx-auto">
              Your business problems don&apos;t exist in silos. Neither should your solutions.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-3 mb-8">
            {BUSINESS_PROBLEMS.map((problem, i) => (
              <div key={i} className="flex items-start gap-3 p-3 bg-white rounded-lg border border-neutral-200">
                <CheckCircle2 className="w-4 h-4 text-[#39FF14] mt-0.5 flex-shrink-0" />
                <p className="text-sm text-neutral-700">{problem}</p>
              </div>
            ))}
          </div>
          <div className="text-center">
            <Button onClick={() => navigate('/clarity-assessment')} className="bg-neutral-900 hover:bg-neutral-800 text-white">
              Let&apos;s Find the Real Bottleneck <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* ─── 4. INTEGRATED SOLUTION AREAS ─── */}
      <section className="py-20" id="solutions">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-[#2BC40E] font-medium text-sm uppercase tracking-wider mb-2">Integrated Solutions</p>
            <h2 className="text-2xl sm:text-3xl font-bold text-neutral-900 mb-3">Six Connected Solution Areas</h2>
            <p className="text-neutral-600 max-w-xl mx-auto">
              Not disconnected services — interconnected components of one business solutions ecosystem.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {SOLUTION_AREAS.map(area => {
              const expanded = expandedSolution === area.id;
              return (
                <Card key={area.id} className="border-neutral-200 hover:shadow-md transition-all cursor-pointer"
                  onClick={() => setExpandedSolution(expanded ? null : area.id)}>
                  <CardContent className="p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 bg-[#39FF14]/10 rounded-lg flex items-center justify-center">
                        <area.icon className="w-5 h-5 text-[#2BC40E]" />
                      </div>
                      <h3 className="font-semibold text-neutral-900 text-sm flex-1">{area.title}</h3>
                      <ChevronDown className={`w-4 h-4 text-neutral-400 transition-transform ${expanded ? "rotate-180" : ""}`} />
                    </div>
                    <p className="text-xs text-neutral-600 mb-3">{area.desc}</p>
                    {expanded && (
                      <div className="pt-3 border-t border-neutral-100 animate-in fade-in duration-200">
                        <div className="flex flex-wrap gap-1.5">
                          {area.services.map(s => (
                            <span key={s} className="px-2 py-1 bg-neutral-50 text-neutral-600 rounded text-[10px] border border-neutral-100">{s}</span>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── 5. ECOSYSTEM VISUAL ─── */}
      <section className="py-16 bg-[#333333] text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-[#39FF14] text-sm font-medium uppercase tracking-wider mb-2">The Business Solutions Ecosystem</p>
          <h2 className="text-2xl sm:text-3xl font-bold mb-3">More Than Consulting. An Integrated Infrastructure for Growth.</h2>
          <p className="text-neutral-400 text-sm mb-10 max-w-xl mx-auto">Each area connects to the next. We can enter at the point of greatest need while considering the entire business.</p>

          <div className="flex flex-col items-center gap-3">
            {["CLARITY", "STRATEGY", "SYSTEMS", "TECHNOLOGY + AI", "MARKETING + REVENUE", "PEOPLE + TALENT", "IMPLEMENTATION", "OPTIMIZATION", "SCALE"].map((item, i) => (
              <div key={item} className="flex flex-col items-center">
                <div className={`px-6 py-2.5 rounded-lg border font-semibold text-sm ${i === 0 ? "bg-[#39FF14] text-black border-[#39FF14]" : i === 8 ? "bg-[#39FF14] text-black border-[#39FF14]" : "bg-white/5 border-white/20 text-white"}`}>
                  {item}
                </div>
                {i < 8 && <ArrowDown className="w-4 h-4 text-[#39FF14] my-1" />}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── 6. WHY THIS MODEL WORKS ─── */}
      <section className="py-16 bg-neutral-50">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-2xl font-bold text-neutral-900 mb-6">Your Business Problems Don&apos;t Exist in Silos.<br />Neither Should Your Solutions.</h2>
          <div className="grid sm:grid-cols-2 gap-4 mb-8 text-left">
            {[
              "Marketing problems may actually be operational problems.",
              "Technology problems may actually be process problems.",
              "Staffing problems may actually be systems problems.",
              "Revenue problems may actually be positioning or execution problems.",
            ].map((ex, i) => (
              <div key={i} className="p-4 bg-white rounded-lg border border-neutral-200">
                <p className="text-sm text-neutral-700 italic">{ex}</p>
              </div>
            ))}
          </div>
          <p className="text-neutral-600 text-sm max-w-xl mx-auto">
            My Biz Consulting looks at the entire business before prescribing a solution.
            Our integrated approach helps identify root causes instead of repeatedly treating symptoms.
          </p>
        </div>
      </section>

      {/* ─── 7. HOW WE WORK ─── */}
      <section className="py-20" id="how-we-work">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-[#2BC40E] font-medium text-sm uppercase tracking-wider mb-2">How We Work</p>
            <h2 className="text-2xl sm:text-3xl font-bold text-neutral-900 mb-3">From Clarity to Scale</h2>
          </div>
          <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-4">
            {PROCESS_STEPS.map((s, i) => {
              const colors = ["bg-blue-50 border-blue-200 text-blue-700", "bg-purple-50 border-purple-200 text-purple-700", "bg-amber-50 border-amber-200 text-amber-700", "bg-red-50 border-red-200 text-red-700", "bg-cyan-50 border-cyan-200 text-cyan-700", "bg-emerald-50 border-emerald-200 text-emerald-700"];
              return (
                <div key={s.step} className={`rounded-xl p-4 border ${colors[i]} text-center`}>
                  <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center mx-auto mb-2 text-sm font-bold shadow-sm">{i + 1}</div>
                  <h3 className="font-bold text-sm mb-1">{s.step}</h3>
                  <p className="text-[10px] leading-relaxed opacity-80">{s.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── 8. BUSINESS CLARITY ASSESSMENT ─── */}
      <section className="py-16 bg-[#333333] text-white">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold mb-3">Not Sure What You Need? Start With Clarity.</h2>
          <p className="text-neutral-300 mb-8 max-w-xl mx-auto">
            Many business owners know something isn&apos;t working but aren&apos;t sure where the real problem begins.
            The Business Clarity Assessment helps identify operational gaps, growth obstacles, priorities, opportunities,
            and the next best actions before money and resources are spent on the wrong solution.
          </p>
          <Button size="lg" onClick={() => navigate('/clarity-assessment')} className="bg-[#39FF14] hover:bg-[#32E010] text-black h-14 px-8 font-semibold">
            Start Your Business Clarity Assessment <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>

      {/* ─── 9. RESULTS ─── */}
      <section className="py-20" id="results">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-[#2BC40E] font-medium text-sm uppercase tracking-wider mb-2">Results</p>
            <h2 className="text-2xl sm:text-3xl font-bold text-neutral-900 mb-3">Real Results From Real Clients</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, idx) => (
              <Card key={idx} className="border-neutral-200">
                <CardContent className="p-5">
                  <Quote className="w-6 h-6 text-[#39FF14]/20 mb-3" />
                  <p className="text-sm text-neutral-700 mb-5 leading-relaxed">&quot;{t.quote}&quot;</p>
                  <div className="border-t border-neutral-100 pt-3 flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-neutral-900 text-sm">{t.name}</p>
                      <p className="text-xs text-neutral-500">{t.role}</p>
                    </div>
                    <span className="px-2.5 py-1 bg-[#39FF14]/5 text-[#2BC40E] text-xs font-medium rounded-full">{t.result}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="mt-8 text-center">
            <Link to="/case-narratives" className="text-sm text-[#2BC40E] hover:underline flex items-center justify-center gap-1">
              View Case Studies <ChevronRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </section>

      {/* ─── 10. ABOUT ─── */}
      <section id="about" className="py-16 bg-neutral-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-10 items-start">
            <div className="lg:col-span-3">
              <p className="text-[#2BC40E] font-medium text-sm uppercase tracking-wider mb-3">About</p>
              <h2 className="text-2xl sm:text-3xl font-bold text-neutral-900 mb-4">
                My Biz Consulting LLC was founded by business and technology strategist{" "}
                <Link to="/jamilah-n-lawry" className="text-[#2BC40E] hover:underline">Jamilah N. Lawry</Link>,
                professionally known in entertainment and media as Nina Capone.
              </h2>
              <p className="text-neutral-600 leading-relaxed mb-4">
                With experience connected to Def Jam, Phat Farm, Courvoisier, MTV, BET, the PGA Tour, Comcast,
                Tesla, General Motors, and the U.S. Department of Commerce, Jamilah built My Biz Consulting
                to connect the strategy, systems, technology, people, and execution that businesses need to grow
                — within one coordinated ecosystem.
              </p>
              <p className="text-neutral-500 text-sm mb-5">
                We don&apos;t just advise from the sidelines — we build, implement, and optimize the systems
                that drive sustainable growth.
              </p>
              <div className="flex gap-3 flex-wrap">
                <Link to="/jamilah-n-lawry"><Button variant="outline" size="sm" className="border-neutral-300">Meet the Founder</Button></Link>
                <Link to="/capability-statement"><Button variant="outline" size="sm" className="border-neutral-300"><FileText className="w-3.5 h-3.5 mr-1" /> Capabilities</Button></Link>
                <Link to="/press"><Button variant="ghost" size="sm">Press <ChevronRight className="w-3.5 h-3.5 ml-1" /></Button></Link>
              </div>
            </div>
            <div className="lg:col-span-2 space-y-3">
              {/* Awards */}
              {[
                { label: "Tech Behemoths Award 2025", url: "https://techbehemoths.com/awards-2025/wordpress/united-states#view=44749" },
                { label: "Influential Women Honoree", url: "https://influentialwomen.com/profile/jamilah-lawry" },
              ].map(award => (
                <a key={award.label} href={award.url} target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-3 p-3 bg-white rounded-lg border border-neutral-200 hover:border-[#39FF14]/40 transition-all group">
                  <Award className="w-6 h-6 text-amber-500" />
                  <p className="text-sm font-medium text-neutral-900 group-hover:text-[#2BC40E] flex items-center gap-1">{award.label} <ExternalLink className="w-3 h-3 opacity-40" /></p>
                </a>
              ))}
              {/* Certifications */}
              <div className="flex flex-wrap gap-1.5 pt-2">
                {["Google Cloud", "NVIDIA", "CompTIA", "Microsoft", "Apple Search Ads"].map(c => (
                  <span key={c} className="px-2.5 py-1 bg-neutral-100 text-neutral-600 rounded-full text-xs">{c}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── CLIENT PORTALS & SERVICES ─── */}
      <section className="py-16 bg-gradient-to-b from-[#333333] to-neutral-800 text-white" id="client-portals">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#39FF14]/10 rounded-full mb-4">
              <Zap className="w-4 h-4 text-[#39FF14]" />
              <span className="text-[#39FF14] font-medium text-sm">Client Portals & Services</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">Access Your Tools & Services</h2>
            <p className="text-neutral-400 text-sm">Current clients — log in to your dashboards and services below</p>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {/* Anytime Mailbox */}
            <div className="bg-white/10 backdrop-blur border border-white/20 rounded-xl p-6 text-center hover:bg-white/15 transition-all">
              <Mail className="w-10 h-10 text-[#39FF14] mx-auto mb-3" />
              <h3 className="font-bold text-white text-lg mb-1">Anytime Mailbox</h3>
              <p className="text-xs text-neutral-300 mb-5">Virtual mailbox and business address services. Manage your mail from anywhere.</p>
              <div className="flex flex-col gap-2">
                <a href="https://mybizconsultingllc.anytimemailbox.com" target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" className="w-full border-white/30 text-white hover:bg-white/10" size="sm">
                    <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> Renter Login
                  </Button>
                </a>
                <a href="https://mybizconsultingllc.anytimemailbox.com/signup" target="_blank" rel="noopener noreferrer">
                  <Button className="w-full bg-[#39FF14] text-black hover:bg-[#32E010]" size="sm">
                    Sign Up for a Mailbox
                  </Button>
                </a>
              </div>
            </div>

            {/* Marketing Dashboard */}
            <div className="bg-white/10 backdrop-blur border border-white/20 rounded-xl p-6 text-center hover:bg-white/15 transition-all">
              <BarChart3 className="w-10 h-10 text-purple-400 mx-auto mb-3" />
              <h3 className="font-bold text-white text-lg mb-1">Marketing Dashboard</h3>
              <p className="text-xs text-neutral-300 mb-5">For clients receiving Social Media Marketing, SEO, AEO, Google Marketing, and more.</p>
              <a href="https://dashboard.mybizconsulting.net/login" target="_blank" rel="noopener noreferrer">
                <Button variant="outline" className="w-full border-purple-400/40 text-purple-300 hover:bg-purple-500/10" size="sm">
                  <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> Client Dashboard Login
                </Button>
              </a>
            </div>

            {/* Business Formation */}
            <div className="bg-white/10 backdrop-blur border border-white/20 rounded-xl p-6 text-center hover:bg-white/15 transition-all">
              <Building2 className="w-10 h-10 text-emerald-400 mx-auto mb-3" />
              <h3 className="font-bold text-white text-lg mb-1">Business Formation</h3>
              <p className="text-xs text-neutral-300 mb-5">LLC, Corporation, Nonprofit, and Professional Corp formation in all 50 states.</p>
              <Button onClick={() => navigate('/business-formation')} className="w-full bg-[#39FF14] text-black hover:bg-[#32E010]" size="sm">
                Start a Filing <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* ─── SECURITY & COMPLIANCE ─── */}
      <section className="py-16 bg-neutral-900 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <Shield className="w-8 h-8 text-green-400 mx-auto mb-3" />
            <h2 className="text-2xl font-bold mb-2">Enterprise-Grade Security & Compliance</h2>
            <p className="text-neutral-400 text-sm max-w-xl mx-auto">
              Your data security is our priority. We maintain rigorous security standards across our entire business solutions ecosystem.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {[
              { icon: Shield, title: "AES-256 Encryption", desc: "All data encrypted at rest and in transit using industry-standard AES-256 encryption." },
              { icon: Eye, title: "24/7 Monitoring", desc: "Continuous system monitoring, threat detection, and automated security alerts." },
              { icon: FileText, title: "Complete Audit Trail", desc: "Every action is logged for accountability, compliance, and transparency." },
              { icon: Users, title: "Role-Based Access", desc: "Granular access controls ensure users only see what they need." },
            ].map(item => (
              <div key={item.title} className="bg-white/5 border border-white/10 rounded-xl p-5">
                <item.icon className="w-6 h-6 text-green-400 mb-3" />
                <h3 className="font-semibold text-white text-sm mb-1">{item.title}</h3>
                <p className="text-xs text-neutral-400">{item.desc}</p>
              </div>
            ))}
          </div>
          <div className="grid sm:grid-cols-3 gap-3 mb-6">
            {[
              "SOC 2 Compliant Infrastructure",
              "GDPR-Ready Data Handling",
              "Secure Cloud Architecture",
              "Regular Security Audits",
              "Data Privacy by Design",
              "Encrypted Backups",
            ].map(item => (
              <div key={item} className="flex items-center gap-2 p-2.5 bg-green-500/10 rounded-lg border border-green-500/20">
                <CheckCircle2 className="w-3.5 h-3.5 text-green-400 flex-shrink-0" />
                <span className="text-xs text-green-300">{item}</span>
              </div>
            ))}
          </div>
          <div className="text-center">
            <Link to="/security" className="text-sm text-green-400 hover:text-green-300 flex items-center justify-center gap-1">
              View Full Security & Privacy Details <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </section>

      {/* ─── 11. FINAL CTA ─── */}
      <section className="py-20 bg-neutral-900">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            You Don&apos;t Need Another Disconnected Solution.
          </h2>
          <p className="text-neutral-400 max-w-xl mx-auto mb-3">
            You need clarity around what&apos;s actually holding the business back — and a coordinated strategy for fixing it.
          </p>
          <p className="text-neutral-500 text-sm max-w-xl mx-auto mb-8">
            My Biz Consulting connects strategy, systems, technology, people, and execution to help turn business complexity into scalable infrastructure.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <Button size="lg" onClick={() => navigate('/clarity-assessment')} className="bg-white hover:bg-neutral-100 text-neutral-900 h-14 px-8 text-base font-medium">
              Start With a Business Clarity Assessment <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button size="lg" variant="ghost" onClick={() => document.getElementById('solutions')?.scrollIntoView({ behavior: 'smooth' })}
              className="h-14 px-6 text-neutral-400 hover:text-white hover:bg-white/10">
              Explore Our Solutions
            </Button>
          </div>

          <p className="text-[#39FF14] text-sm font-medium tracking-wider">Consultant-Led &bull; AI-Powered &bull; Results-Driven</p>

          {/* Contact */}
          <div className="flex flex-wrap justify-center gap-6 mt-8 text-sm text-neutral-500">
            <a href="tel:215-443-1076" className="flex items-center gap-1 hover:text-white"><Phone className="w-4 h-4" /> 215-443-1076</a>
            <a href="mailto:Jamilah@mybizconsulting.net" className="flex items-center gap-1 hover:text-white"><Mail className="w-4 h-4" /> Jamilah@mybizconsulting.net</a>
            <span className="flex items-center gap-1"><MapPin className="w-4 h-4" /> Folcroft, PA 19032</span>
          </div>
        </div>
      </section>

      {/* ─── FOOTER ─── */}
      <footer className="py-10 bg-neutral-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Footer brand statement */}
          <p className="text-neutral-500 text-xs text-center max-w-2xl mx-auto mb-6">
            My Biz Consulting LLC is a vertically integrated business solutions firm connecting strategy, operations,
            technology, AI, marketing, people, and execution within one coordinated ecosystem.
          </p>
          <p className="text-[#39FF14] text-xs text-center font-medium tracking-wider mb-8">
            Consultant-Led &bull; AI-Powered &bull; Results-Driven
          </p>

          {/* Social */}
          {/* Featured In Badge */}
          <div className="flex justify-center mb-6">
            <div className="inline-flex items-center gap-3 px-5 py-2.5 bg-neutral-800 rounded-full border border-neutral-700">
              <div className="flex items-center gap-1.5">
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none"><path d="M22.282 9.821a5.985 5.985 0 0 0-.516-4.91 6.046 6.046 0 0 0-6.51-2.9A6.065 6.065 0 0 0 4.981 4.18a5.985 5.985 0 0 0-3.998 2.9 6.046 6.046 0 0 0 .743 7.097 5.98 5.98 0 0 0 .51 4.911 6.051 6.051 0 0 0 6.515 2.9A5.985 5.985 0 0 0 13.26 24a6.056 6.056 0 0 0 5.772-4.206 5.99 5.99 0 0 0 3.997-2.9 6.056 6.056 0 0 0-.747-7.073zM13.26 22.43a4.476 4.476 0 0 1-2.876-1.04l.141-.081 4.779-2.758a.795.795 0 0 0 .392-.681v-6.737l2.02 1.168a.071.071 0 0 1 .038.052v5.583a4.504 4.504 0 0 1-4.494 4.494zM3.6 18.304a4.47 4.47 0 0 1-.535-3.014l.142.085 4.783 2.759a.771.771 0 0 0 .78 0l5.843-3.369v2.332a.08.08 0 0 1-.033.062L9.74 19.95a4.5 4.5 0 0 1-6.14-1.646zM2.34 7.896a4.485 4.485 0 0 1 2.366-1.973V11.6a.766.766 0 0 0 .388.676l5.815 3.355-2.02 1.168a.076.076 0 0 1-.071 0l-4.83-2.786A4.504 4.504 0 0 1 2.34 7.872zm16.597 3.855l-5.833-3.387L15.119 7.2a.076.076 0 0 1 .071 0l4.83 2.791a4.494 4.494 0 0 1-.676 8.105v-5.678a.79.79 0 0 0-.407-.667zm2.01-3.023l-.141-.085-4.774-2.782a.776.776 0 0 0-.785 0L9.409 9.23V6.897a.066.066 0 0 1 .028-.061l4.83-2.787a4.5 4.5 0 0 1 6.68 4.66zm-12.64 4.135l-2.02-1.164a.08.08 0 0 1-.038-.057V6.075a4.5 4.5 0 0 1 7.375-3.453l-.142.08L8.704 5.46a.795.795 0 0 0-.393.681zm1.097-2.365l2.602-1.5 2.607 1.5v2.999l-2.597 1.5-2.607-1.5z" fill="#ffffff"/></svg>
                <svg className="w-5 h-5" viewBox="0 0 24 24"><circle cx="12" cy="12" r="3" fill="#FF6B35"/><line x1="12" y1="2" x2="12" y2="7" stroke="#FF6B35" strokeWidth="2"/><line x1="12" y1="17" x2="12" y2="22" stroke="#FF6B35" strokeWidth="2"/><line x1="2" y1="12" x2="7" y2="12" stroke="#FF6B35" strokeWidth="2"/><line x1="17" y1="12" x2="22" y2="12" stroke="#FF6B35" strokeWidth="2"/><line x1="4.93" y1="4.93" x2="8.46" y2="8.46" stroke="#FF6B35" strokeWidth="2"/><line x1="15.54" y1="15.54" x2="19.07" y2="19.07" stroke="#FF6B35" strokeWidth="2"/><line x1="4.93" y1="19.07" x2="8.46" y2="15.54" stroke="#FF6B35" strokeWidth="2"/><line x1="15.54" y1="8.46" x2="19.07" y2="4.93" stroke="#FF6B35" strokeWidth="2"/></svg>
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none"><path d="M12 2L8 6H4v4l-4 4 4 4v4h4l4 4 4-4h4v-4l4-4-4-4V6h-4l-4-4z" fill="#40E0D0" opacity="0.3"/><path d="M12 6L9 9H6v3l-3 3 3 3v3h3l3 3 3-3h3v-3l3-3-3-3V9h-3l-3-3z" fill="#40E0D0"/></svg>
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none"><path d="M12 2L14.5 9H22L16 13.5L18 21L12 16.5L6 21L8 13.5L2 9H9.5L12 2Z" fill="#4285F4"/></svg>
              </div>
              <span className="text-white text-sm font-medium">Featured in ChatGPT, Claude, Perplexity &amp; Gemini</span>
            </div>
          </div>

          {/* Social Icons */}
          <div className="flex justify-center items-center gap-4 mb-6">
            <a href="https://www.chamberofcommerce.com/business-directory/pennsylvania/folcroft/business-management-consultant/2017414126-my-biz-consulting?source=memberwebsite" target="_blank" rel="noopener noreferrer">
              <img src="https://coc.codes/images/badge/2017414126" alt="Chamber of Commerce" className="h-8 w-auto rounded" width="auto" height="32" />
            </a>
            {[
              { href: "https://www.facebook.com/MyBizConsulting", path: "M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" },
              { href: "https://www.instagram.com/mybizconsulting/", path: "M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" },
              { href: "https://www.linkedin.com/company/my-biz-consulting-llc", path: "M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" },
              { href: "https://x.com/mybizconsult304", path: "M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" },
              { href: "https://www.youtube.com/channel/UC38WDYvX0Z2m9iEduQHytPA", path: "M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814z" },
            ].map(s => (
              <a key={s.href} href={s.href} target="_blank" rel="noopener noreferrer"
                className="w-8 h-8 bg-neutral-800 hover:bg-[#39FF14]/20 rounded-lg flex items-center justify-center transition-colors group">
                <svg className="w-4 h-4 text-neutral-500 group-hover:text-[#39FF14]" fill="currentColor" viewBox="0 0 24 24"><path d={s.path} /></svg>
              </a>
            ))}
          </div>

          {/* Footer Links */}
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center space-x-3">
              <img src="/mybiz-logo.png" alt="My Biz Consulting" className="h-7 w-auto brightness-0 invert" />
            </div>
            <div className="flex items-center flex-wrap justify-center gap-x-4 gap-y-2 text-xs text-neutral-500">
              <Link to="/login" className="hover:text-neutral-300">Login</Link>
              <Link to="/register" className="hover:text-neutral-300">Get Started</Link>
              <Link to="/faq" className="hover:text-neutral-300">FAQ</Link>
              <Link to="/capability-statement" className="hover:text-neutral-300">Capabilities</Link>
              <Link to="/jamilah-n-lawry" className="hover:text-neutral-300">About Jamilah</Link>
              <Link to="/press" className="hover:text-neutral-300">Press</Link>
              <Link to="/media" className="hover:text-neutral-300">Podcast</Link>
              <Link to="/case-narratives" className="hover:text-neutral-300">Case Studies</Link>
              <Link to="/business-formation" className="hover:text-neutral-300">Business Formation</Link>
              <Link to="/blog" className="hover:text-neutral-300">Blog</Link>
              <Link to="/location" className="hover:text-neutral-300">Location</Link>
              <Link to="/sponsorship" className="hover:text-neutral-300">Funding</Link>
              <Link to="/partnership" className="hover:text-neutral-300">Partnership</Link>
              <Link to="/security" className="hover:text-neutral-300">Security</Link>
              <a href="https://mybizconsultingllc.anytimemailbox.com/" target="_blank" rel="noopener noreferrer" className="hover:text-neutral-300">Mailbox</a>
              <a href="mailto:Jamilah@mybizconsulting.net" className="hover:text-neutral-300">Contact</a>
            </div>
            <p className="text-neutral-600 text-xs">© {new Date().getFullYear()} My Biz Consulting LLC. My Biz Consulting℠ is a service mark of My Biz Consulting LLC. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
